import { __ } from '@wordpress/i18n';
import getCurrentLocation from '../../../js/__utils/getCurrentLocation';
import setCurrentLocation from '../../../js/__utils/setCurrentLocation';

const initializeHours = () => {
	const hoursElements = document.querySelectorAll(
		'.location-hours:not(.location-hours--initialized)'
	);

	hoursElements.forEach((hoursElement) => {
		hoursElement.classList.add('location-hours--initialized');

		if (!hoursElement?.dataset?.hours || !hoursElement?.dataset?.timezone) {
			return;
		}

		const status = hoursElement.querySelector('.location-hours__status');
		const todayTime = hoursElement.querySelector(
			'.location-hours__today-time'
		);

		if (!status || !todayTime) {
			hoursElement.classList.add('location-hours--visible');

			return;
		}

		let hoursData;

		try {
			hoursData = JSON.parse(hoursElement.dataset.hours);
		} catch {
			hoursData = null;
		}

		if (!hoursData) {
			hoursElement.classList.add('location-hours--visible');

			return;
		}

		const timezone = hoursElement.dataset.timezone;
		const now = new Date();
		const nowInTz = new Date(
			now.toLocaleString('en-US', { timeZone: timezone })
		);
		const todayWeekday = nowInTz
			.toLocaleDateString('en-US', { weekday: 'long' })
			.toLowerCase();
		const todayHours = hoursData[todayWeekday]?.openIntervals || [];
		const isClosedToday = hoursData[todayWeekday]?.isClosed;

		const pad = (n) => n.toString().padStart(2, '0');
		const nowStr = `${pad(nowInTz.getHours())}:${pad(nowInTz.getMinutes())}`;

		const formatTime = (timeStr) => {
			return timeToDate(timeStr)
				.toLocaleTimeString('en-US', {
					hour: 'numeric',
					minute: '2-digit',
					hour12: true,
				})
				.toLowerCase();
		};

		let currentInterval = null;

		for (const interval of todayHours) {
			if (!interval.start || !interval.end) {
				continue;
			}

			if (nowStr >= interval.start && nowStr < interval.end) {
				currentInterval = interval;
				break;
			}
		}

		let output = '';
		let statusText = '';

		if (isClosedToday || !todayHours.length) {
			output = getNextOpening(hoursData, nowInTz);
			statusText = __('Closed', 'propel');
			status.classList.add('location-hours__status--closed');
		} else if (currentInterval) {
			output =
				__('Closes', 'propel') + ` ${formatTime(currentInterval.end)}`;
			statusText = __('Open', 'propel');
			status.classList.add('location-hours__status--open');

			const nextIntervals = todayHours.filter(
				(interval) => interval.start > currentInterval.end
			);

			if (nextIntervals.length) {
				const next = nextIntervals[0];
				output +=
					'. ' +
					__('Reopens', 'propel') +
					` ${formatTime(next.start)}`;
			}
		} else {
			statusText = __('Closed', 'propel');
			status.classList.add('location-hours__status--closed');

			for (let idx = 0; idx < todayHours.length; idx++) {
				const interval = todayHours[idx];

				if (interval.start > nowStr) {
					if (idx === 0) {
						output =
							__('Opens', 'propel') +
							` ${formatTime(interval.start)}`;
					} else {
						output =
							__('Reopens today', 'propel') +
							` ${formatTime(interval.start)}`;
					}

					break;
				}
			}

			if (!output) {
				output = getNextOpening(hoursData, nowInTz);
			}
		}

		todayTime.textContent = output;
		status.textContent = statusText;
		hoursElement.classList.add('location-hours--visible');
	});
};

const timeToDate = (timeStr) => {
	return new Date(`1970-01-01T${timeStr}:00`);
};

const getNextOpening = (hoursData, nowInTz) => {
	for (let i = 1; i <= 7; i++) {
		const nextDate = new Date(nowInTz);
		nextDate.setDate(nowInTz.getDate() + i);
		const weekday = nextDate
			.toLocaleDateString('en-US', { weekday: 'long' })
			.toLowerCase();
		const isClosed = hoursData[weekday]?.isClosed;
		const intervals = hoursData[weekday]?.openIntervals || [];

		if (!isClosed && intervals.length) {
			const openTime = intervals[0].start;
			const openStr = timeToDate(openTime)
				.toLocaleTimeString([], {
					hour: 'numeric',
					minute: '2-digit',
				})
				.toLowerCase();
			const label =
				i === 1
					? __('Opens tomorrow', 'propel')
					: __('Opens', 'propel') +
						' ' +
						weekday.charAt(0).toUpperCase() +
						weekday.slice(1);

			return `${label} ${openStr}`;
		}
	}

	return __('Closed', 'propel');
};

const initializeSetLocationButtons = () => {
	const updateSetLocationButtons = (locationId = '') => {
		setLocationButtons.forEach((button) => {
			if (button.dataset.id === locationId) {
				button.classList.add(
					'location-meta__set-location-button--selected'
				);
				button.textContent = __('My Store', 'propel');
			} else {
				button.classList.remove(
					'location-meta__set-location-button--selected'
				);
				button.textContent = __('Set My Location', 'propel');
			}
		});
	};

	const setLocationButtons = document.querySelectorAll(
		'.location-meta__set-location-button:not(.location-meta__set-location-button--initialized)'
	);

	setLocationButtons.forEach((button) => {
		button.classList.add('location-meta__set-location-button--initialized');

		const locationId = button?.dataset?.id ?? null;
		const yextId = button?.dataset?.yextId ?? null;
		const locationName = button?.dataset?.locationName ?? null;

		if (!locationId || !locationName || !yextId) {
			return;
		}

		const currentCookie = getCurrentLocation();

		if (currentCookie?.locationId === locationId) {
			updateSetLocationButtons(locationId);
		}

		button.addEventListener('click', (e) => {
			e.preventDefault();

			if (
				e.currentTarget.classList.contains(
					'location-meta__set-location-button--selected'
				)
			) {
				setCurrentLocation();
				updateSetLocationButtons();
			} else {
				setCurrentLocation(locationId, yextId, locationName);

				updateSetLocationButtons(locationId);
			}
		});
	});
};

initializeHours();
initializeSetLocationButtons();

window.addEventListener('propel-filters-render-posts', initializeHours);
window.addEventListener(
	'propel-filters-render-posts',
	initializeSetLocationButtons
);

export {
	initializeHours,
	initializeSetLocationButtons,
	timeToDate,
	getNextOpening,
};
